/**
 * Marketing analytics layer (PostHog-backed, privacy-safe).
 *
 * This is NOT a parallel analytics system: every marketing event is emitted
 * through the existing `track()` helper in `src/lib/analytics.ts`, so it lands
 * in the same PostHog project and the same first-party Supabase event log.
 * What this module adds is a small, centralized funnel vocabulary:
 *
 *   marketing_page_view → marketing_release_view → marketing_listen_click
 *                                                → marketing_presave_click
 *                                                → marketing_own_click
 *                                                → marketing_checkout_start
 *   marketing_page_view → marketing_signup_success / marketing_join_click
 *   press context       → marketing_press_click / marketing_contact_success
 *
 * Privacy rules enforced here (see docs/analytics/marketing-events.md):
 *  - Marketing analytics only runs when the visitor accepted ALL cookies.
 *    "Essential only" and "no choice yet" never emit marketing events.
 *  - Attribution (src/lib/marketing/attribution.ts) is limited to the UTM
 *    allowlist plus referring host/path: current-session values, and durable
 *    first-touch values under `first_touch_*`.
 *  - Property values are sanitized: no email, name, message/body text, form
 *    contents, full query strings, tokens or auth data ever leave the browser.
 */

import { getCookieConsent } from "@/components/site/CookieConsent";
import { track } from "@/lib/analytics";
import {
  attributionSuperProps,
  ensureAttribution,
  normalizePath,
  sanitizeProps,
  type MarketingProps,
} from "@/lib/marketing/attribution";

export {
  ALLOWED_UTM_PARAMS,
  ATTRIBUTION_STORAGE_KEY,
  FIRST_TOUCH_STORAGE_KEY,
  attributionSuperProps,
  deriveAttribution,
  ensureAttribution,
  getAttribution,
  getFirstTouchAttribution,
  normalizePath,
  sanitizeProps,
  type Attribution,
  type MarketingProps,
} from "@/lib/marketing/attribution";

export type MarketingEvent =
  | "marketing_page_view"
  | "marketing_release_view"
  | "marketing_listen_click"
  | "marketing_presave_click"
  | "marketing_own_click"
  | "marketing_checkout_start"
  | "marketing_join_click"
  | "marketing_press_click"
  | "marketing_signup_success"
  | "marketing_contact_success";

/** True when the visitor has granted consent covering marketing analytics. */
export function isMarketingAnalyticsAllowed(): boolean {
  return getCookieConsent() === "all";
}

/**
 * Single funnel emitter. Consent-gated, sanitized, attribution-enriched, and
 * routed through the existing `track()` pipeline.
 */
export function captureMarketing(event: MarketingEvent, props?: MarketingProps): boolean {
  if (typeof window === "undefined") return false;
  if (!isMarketingAnalyticsAllowed()) return false;
  ensureAttribution();
  const payload: MarketingProps = {
    ...attributionSuperProps(),
    path: normalizePath(window.location.pathname),
    ...sanitizeProps(props),
  };
  track(event, payload);
  return true;
}

// -- Funnel helpers ----------------------------------------------------------

export function trackMarketingPageView(path: string): boolean {
  return captureMarketing("marketing_page_view", { path: normalizePath(path) });
}

export function trackMarketingReleaseView(slug: string, releaseState?: string | null): boolean {
  return captureMarketing("marketing_release_view", { slug, release_state: releaseState ?? undefined });
}

export function trackMarketingListenClick(input: {
  platform: string;
  slug?: string;
  trackSlug?: string;
  surface?: string;
}): boolean {
  return captureMarketing("marketing_listen_click", {
    platform: input.platform,
    slug: input.slug,
    track_slug: input.trackSlug,
    surface: input.surface,
  });
}

export function trackMarketingPresaveClick(input: { slug: string; platform?: string; surface?: string }): boolean {
  return captureMarketing("marketing_presave_click", {
    slug: input.slug,
    platform: input.platform ?? "hyperfollow",
    surface: input.surface,
  });
}

export function trackMarketingOwnClick(input: { slug?: string; type?: string; surface?: string }): boolean {
  return captureMarketing("marketing_own_click", {
    slug: input.slug,
    type: input.type,
    surface: input.surface,
  });
}

export function trackMarketingPressClick(input: { action: string; surface?: string; platform?: string }): boolean {
  return captureMarketing("marketing_press_click", {
    action: input.action,
    surface: input.surface,
    platform: input.platform,
  });
}

export function trackMarketingSignupSuccess(input: { source: string; duplicate?: boolean }): boolean {
  return captureMarketing("marketing_signup_success", {
    source: input.source,
    duplicate: input.duplicate ?? false,
  });
}

export function trackMarketingContactSuccess(input: { type?: string; surface?: string }): boolean {
  return captureMarketing("marketing_contact_success", {
    type: input.type,
    surface: input.surface,
  });
}

export function trackMarketingCheckoutStart(input: { slug?: string; type?: string; surface?: string }): boolean {
  return captureMarketing("marketing_checkout_start", {
    slug: input.slug,
    type: input.type,
    surface: input.surface,
  });
}

export function trackMarketingJoinClick(input: { action: string; surface?: string }): boolean {
  return captureMarketing("marketing_join_click", {
    action: input.action,
    surface: input.surface,
  });
}
